import { useState, type ReactNode } from "react";
import { classesApi } from "./api";
import { CLASS_RULES, RULES_AGREE, RULES_INTRO } from "./class-rules";
import { Lion } from "./Lion";
import "./StudentStart.css";
import { SignOutButton } from "./SignOut";

// A student's first visit (decision 68): type the class code from the board, then agree to the class
// rules. Both happen once; after that BuildLab opens on Home.

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? "";

function StartFrame({ step, children }: { step: 1 | 2; children: ReactNode }) {
  return (
    <main className="fv">
      <div className="fv-card">
        <p className="eyebrow gold fv-step">
          Step {step} of 2 · {step === 1 ? "Class code" : "Class rules"}
        </p>
        {children}
      </div>
    </main>
  );
}

/** guest: an outside guest (Review 14), who joins a demo class with the code the teacher gave them. */
export function JoinClass({ name, guest = false, onJoined }: { name: string; guest?: boolean; onJoined: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hi = firstName(name);

  const join = async () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await classesApi.join(code);
      onJoined();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <StartFrame step={1}>
      <Lion pose="wave" size={156} label="Roary the lion waving hello" bob />
      <h1>{hi ? `Hi, ${hi}!` : "Welcome!"}</h1>
      <p className="fv-lede">{guest ? "Welcome to BuildLab! Type the code for the demo class to join it." : "Welcome to BuildLab. Type the class code from the board to join your class."}</p>
      <form
        className="fv-join-form"
        onSubmit={(e) => {
          e.preventDefault();
          void join();
        }}
      >
        <label htmlFor="join-code" className="eyebrow">
          Class code
        </label>
        <input
          id="join-code"
          className="fv-join-code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={9}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-describedby={error ? "join-error" : undefined}
          autoFocus
        />
        <button type="submit" className="primary" disabled={busy || !code.trim()}>
          {busy ? "Joining…" : guest ? "Join the demo class" : "Join my class"}
        </button>
      </form>
      {error && (
        <p id="join-error" className="fv-error" role="alert">
          {error}
        </p>
      )}
      <p className="fv-hint">{guest ? "No code? Ask the teacher who invited you." : "No code? Ask your teacher. Each class has its own."}</p>
      <SignOutButton className="linklike small">Wrong account? Sign out</SignOutButton>
    </StartFrame>
  );
}

export function ClassRules({ className, onAccepted }: { className: string; onAccepted: () => void }) {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <StartFrame step={2}>
      <div className="fv-rules-head">
        <Lion pose="cheer" size={104} label="Roary the lion cheering" />
        <div>
          <p className="fv-rules-joined">You joined {className}!</p>
          <h1>Our class rules</h1>
          <p className="fv-lede">{RULES_INTRO}</p>
        </div>
      </div>
      <ol className="fv-rules">
        {CLASS_RULES.map((r, i) => (
          <li key={r.title}>
            <span className="fv-rule-num" aria-hidden="true">
              {i + 1}
            </span>
            <div>
              <h2>{r.title}</h2>
              <p>{r.text}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="fv-rules-agree">
        <label className="check">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          {RULES_AGREE}
        </label>
        <button
          type="button"
          className="primary"
          disabled={!agree || busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await classesApi.acceptRules();
              onAccepted();
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        >
          I agree. Let's build!
        </button>
      </div>
      {error && (
        <p className="fv-error" role="alert">
          {error}
        </p>
      )}
    </StartFrame>
  );
}

/** The student's class, shown next to their name on Home. */
export function ClassBadge({ name }: { name: string }) {
  return <span className="class-badge">{name}</span>;
}
